import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChangePasswordModal from '../components/ChangePasswordModal';
import logo from '../assets/images/logo.png';
import '../styles/dashboard.css';

// adminOnly items are hidden for USER accounts.
// (Hiding is cosmetic — the backend also blocks those API calls.)
const NAV_ITEMS = [
  { to: '/generate', label: 'Generate Offer', icon: MailIcon },
  { to: '/companies', label: 'Companies', icon: BuildingIcon, adminOnly: true },
  { to: '/history', label: 'History', icon: HistoryIcon },
  { to: '/users', label: 'Users', icon: UsersIcon, adminOnly: true },
];

export default function DashboardLayout() {
  const { admin, isAdmin, logout } = useAuth();
  const navItems = NAV_ITEMS.filter((item) => isAdmin || !item.adminOnly);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="hp-shell">
      {/* ── Mobile-only top bar: hamburger + brand, visible below 860px ── */}
      <div className="hp-mobile-topbar">
        <button
          type="button"
          className="hp-hamburger-btn"
          onClick={() => setMobileMenuOpen(true)}
          aria-label="Open menu"
        >
          <HamburgerIcon />
        </button>
        <div className="hp-mobile-brand">
          <img src={logo} alt="HirePeaks logo" className="hp-mobile-logo" />
          <span>HirePeaks</span>
        </div>
      </div>

      {/* ── Overlay — click to close the drawer ── */}
      {mobileMenuOpen && <div className="hp-overlay" onClick={closeMobileMenu} />}

      <aside className={'hp-sidebar' + (mobileMenuOpen ? ' hp-sidebar--open' : '')}>
        <div className="hp-sidebar-glow" aria-hidden="true" />

        <div className="hp-sidebar-top">
          <div className="hp-brand">
            <div className="hp-brand-logo-wrap">
              <img src={logo} alt="HirePeaks logo" className="hp-brand-logo" />
            </div>
            <span className="hp-brand-name">HirePeaks</span>
          </div>
          <div className="hp-brand-sub">OFFER LETTER SYSTEM</div>

          <button
            type="button"
            className="hp-close-btn"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <CloseIcon />
          </button>
        </div>

        <nav className="hp-nav" aria-label="Main navigation">
          <div className="hp-nav-label">Menu</div>
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={closeMobileMenu}
              className={({ isActive }) => 'hp-nav-link' + (isActive ? ' hp-nav-link--active' : '')}
            >
              <Icon />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="hp-sidebar-footer">
          <div className="hp-user">
            <div className="hp-user-avatar" aria-hidden="true">{getInitials(admin?.name)}</div>
            <div style={{ minWidth: 0 }}>
              <div className="hp-user-name">{admin?.name || 'Admin'}</div>
              <div style={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.5)' }}>{isAdmin ? 'Admin' : 'User'}</div>
            </div>
          </div>

          <button
            type="button"
            className="hp-change-password"
            onClick={() => { setShowChangePassword(true); closeMobileMenu(); }}
          >
            <KeyIcon />
            <span>Change Password</span>
          </button>

          <button type="button" className="hp-signout" onClick={logout}>
            <LogoutIcon />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      <main className="hp-main">
        <Outlet />
      </main>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

function getInitials(name = '') {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0]?.toUpperCase())
      .join('') || 'A'
  );
}

// ── Icon set ──
function MailIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M4 6.5 L12 13 L20 6.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="5" y="3" width="10" height="18" rx="1" stroke="currentColor" strokeWidth="1.7" />
      <rect x="15" y="9" width="6" height="12" rx="1" stroke="currentColor" strokeWidth="1.7" />
      <line x1="8" y1="7" x2="8" y2="7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="7" x2="12" y2="7.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="11" x2="8" y2="11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="11" x2="12" y2="11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="15" x2="8" y2="15.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="12" y1="15" x2="12" y2="15.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 2.6-6.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M3 4v4.5h4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function UsersIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M18 14.4c2.1.7 3.5 2.8 3.5 5.6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="8" cy="15" r="4" stroke="currentColor" strokeWidth="1.7" />
      <path d="M11 12l9-9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M16 7l3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M13 10l2.5 2.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M16 17l5-5-5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="4" y1="7" x2="20" y2="7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="4" y1="17" x2="20" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
